import type { RootState } from '..';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { Tag } from '../../apis/facades/useUserInputsApiFacade';
import type { ConfigurationItemMap } from '../../components/documentation/configuration-item-tables/shared/helpers';
import type { UserInputsByMetadataId } from '@server/user-inputs';
import { UserInputTypes } from '@server/user-inputs';
import type { DocumentationTabTypes } from '../../types/enums/DocumentationTabTypes';
import type { DocumentationItemsMap } from './createDocumentationItemsMap';
import uniq from 'lodash/uniq';
import { ConfigurationTypeToDocumentationTabType } from '../../types/enums/DocumentationTabTypes';
import { findConfigurationType, extractTagIdsFromUserInputs } from './summaryUtils';

/**
 * User inputs are combination of tags and users (as collaborators owners)
 */

export interface UserInputsState {
  tagsList: Tag[];
  tagsConfigurations: {
    [tagId: string]: { [tagKey in UserInputTypes]: ConfigurationItemMap };
  };
  userInputsByMetadataId?: UserInputsByMetadataId; //contains only current env tags
  tagsAndUsersCountSummary?: {
    [crmOrgId: string]: {
      [tagOrUserId: string]: {
        [configurationType in Exclude<
          DocumentationTabTypes,
          DocumentationTabTypes.CARDS_LIST | DocumentationTabTypes.RELATED_OBJECTS
        >]: string[]; // string[] (array of configuration IDs)
      };
    };
  };
  parserItemsMap?: { [crmOrgId: string]: DocumentationItemsMap };
}

const initialState: UserInputsState = {
  tagsList: [],
  tagsConfigurations: {},
};

export const userInputsSlice = createSlice({
  name: 'userInputs',
  initialState,
  reducers: {
    setTagsList: (state, action: PayloadAction<{ tags: Tag[] }>) => {
      state.tagsList = action.payload.tags;
    },
    setTagConfigurations: (
      state,
      action: PayloadAction<{
        tagId: string;
        configurations: { [key in UserInputTypes]: ConfigurationItemMap };
      }>,
    ) => {
      const { configurations, tagId } = action.payload;
      state.tagsConfigurations[tagId] = configurations;
    },
    clearTagConfigurations: (state) => {
      state.tagsConfigurations = {};
    },
    addTag: (state, action: PayloadAction<{ tag: Tag }>) => {
      state.tagsList.push(action.payload.tag);
    },
    updateTag: (state, action: PayloadAction<{ tag: Tag }>) => {
      const { tag } = action.payload;
      state.tagsList = state.tagsList.map((_tag) => (_tag.id === tag.id ? tag : _tag));
    },
    deleteTag: (state, action: PayloadAction<{ tagId: string }>) => {
      const { tagId } = action.payload;
      state.tagsList = state.tagsList.filter((tag) => tag.id !== tagId);
    },
    setUserInputsByMetadataId: (
      state,
      action: PayloadAction<{
        userInputs: UserInputsByMetadataId;
      }>,
    ) => {
      state.userInputsByMetadataId = action.payload.userInputs;
    },
    setSingleConfigurationUserInputs: (
      state,
      action: PayloadAction<{
        userInputs: UserInputsByMetadataId;
        crmOrgId: string;
      }>,
    ) => {
      const { userInputs, crmOrgId } = action.payload;
      const [[metadataId, userInputData]] = Object.entries(userInputs);
      if (!state.userInputsByMetadataId) {
        state.userInputsByMetadataId = {};
      }

      // Remove old configuration from summary before updating
      if (state.userInputsByMetadataId[metadataId] && state.tagsAndUsersCountSummary?.[crmOrgId]) {
        const summary = state.tagsAndUsersCountSummary[crmOrgId];
        Object.keys(summary).forEach((tagId) => {
          Object.keys(summary[tagId]).forEach((tabType) => {
            const configArray = (summary[tagId] as any)[tabType] as string[];
            const index = configArray.indexOf(metadataId);
            if (index > -1) {
              configArray.splice(index, 1);
            }
          });
        });
      }

      state.userInputsByMetadataId[metadataId] = userInputData;

      // Add updated configuration to summary
      const parserItemsMap = state.parserItemsMap;
      if (parserItemsMap?.[crmOrgId]) {
        const parserItems = parserItemsMap[crmOrgId];
        const configurationType = findConfigurationType(parserItems, metadataId);

        if (configurationType) {
          const documentationTabType = ConfigurationTypeToDocumentationTabType[configurationType];
          if (documentationTabType) {
            const allTagIds = extractTagIdsFromUserInputs(userInputData);

            // Ensure summary structure exists
            if (!state.tagsAndUsersCountSummary) {
              state.tagsAndUsersCountSummary = {};
            }
            if (!state.tagsAndUsersCountSummary[crmOrgId]) {
              state.tagsAndUsersCountSummary[crmOrgId] = {};
            }

            allTagIds.forEach((tagId) => {
              if (!state.tagsAndUsersCountSummary![crmOrgId][tagId]) {
                state.tagsAndUsersCountSummary![crmOrgId][tagId] = {} as any;
              }
              if (
                !(state.tagsAndUsersCountSummary![crmOrgId][tagId] as any)[documentationTabType]
              ) {
                (state.tagsAndUsersCountSummary![crmOrgId][tagId] as any)[documentationTabType] =
                  [];
              }

              const configArray = (state.tagsAndUsersCountSummary![crmOrgId][tagId] as any)[
                documentationTabType
              ] as string[];
              if (!configArray.includes(metadataId)) {
                configArray.push(metadataId);
              }
            });
          }
        }
      }
    },

    deleteTagFromConfigurationUserInputs: (
      state,
      action: PayloadAction<{
        tagId: string;
        crmOrgId: string;
      }>,
    ) => {
      const { tagId, crmOrgId } = action.payload;
      const userInputsByMetadataId = state.userInputsByMetadataId ?? ({} as UserInputsByMetadataId);

      const filteredUserInputsByMetadataId = Object.entries(userInputsByMetadataId).reduce(
        (acc, [key]) => {
          const typedKey = key as keyof UserInputsByMetadataId;
          const itemsOfKey = userInputsByMetadataId[typedKey];
          const currentTags = itemsOfKey[UserInputTypes.Tags];
          const currentCollaborators = itemsOfKey[UserInputTypes.Collaborators];
          const currentOwner = itemsOfKey[UserInputTypes.Owner];

          acc[typedKey] = {
            [UserInputTypes.Tags]: currentTags.filter((itemId: string) => itemId !== tagId),
            [UserInputTypes.Collaborators]: currentCollaborators.filter(
              (itemId: string) => itemId !== tagId,
            ),
            [UserInputTypes.Owner]: currentOwner === tagId ? '' : currentOwner,
          };
          return acc;
        },
        {} as UserInputsByMetadataId,
      );

      state.userInputsByMetadataId = filteredUserInputsByMetadataId;

      // Remove tag from summary
      if (state.tagsAndUsersCountSummary?.[crmOrgId]) {
        delete state.tagsAndUsersCountSummary[crmOrgId][tagId];
      }

      // Remove tag configurations when tag is removed from user inputs
      if (state.tagsConfigurations[tagId]) {
        delete state.tagsConfigurations[tagId];
      }
    },
    setParserItemsMap: (
      state,
      action: PayloadAction<{ parserItemsMap: DocumentationItemsMap; crmOrgId: string }>,
    ) => {
      state.parserItemsMap = {
        ...state.parserItemsMap,
        [action.payload.crmOrgId]: action.payload.parserItemsMap,
      };
    },
    addItemsToParserItemsMap: (
      state,
      action: PayloadAction<{ crmOrgId: string; newParserItemsMap: DocumentationItemsMap }>,
    ) => {
      const { crmOrgId, newParserItemsMap } = action.payload;

      if (!state.parserItemsMap) {
        state.parserItemsMap = {};
      }

      if (!state.parserItemsMap[crmOrgId]) {
        state.parserItemsMap[crmOrgId] = {} as DocumentationItemsMap;
      }

      Object.keys(state.parserItemsMap[crmOrgId]).forEach((key) => {
        const typedKey = key as keyof DocumentationItemsMap;
        if (!state.parserItemsMap?.[crmOrgId]?.[typedKey]) {
          return;
        }
        const currentItems = state.parserItemsMap[crmOrgId][typedKey];
        const newItems = newParserItemsMap[typedKey];
        const concatenatedItems = uniq([...currentItems, ...newItems]);

        state.parserItemsMap[crmOrgId][typedKey] = concatenatedItems;
      });
    },
    generateTagsAndUsersSummary: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      const { crmOrgId } = action.payload;
      const parserItemsMap = state.parserItemsMap;
      const userInputsByMetadataId = state.userInputsByMetadataId;

      if (!parserItemsMap || !userInputsByMetadataId) {
        return;
      }

      const parserItems = parserItemsMap[crmOrgId];
      if (!parserItems) {
        return;
      }

      if (!state.tagsAndUsersCountSummary) {
        state.tagsAndUsersCountSummary = {};
      }

      const tagArrays: Record<string, Record<string, string[]>> = {};

      Object.entries(userInputsByMetadataId).forEach(([metadataId, userInputs]) => {
        const configurationType = findConfigurationType(parserItems, metadataId);

        if (!configurationType) {
          return;
        }

        // Check if the configuration type exists in the mapping
        const documentationTabType = ConfigurationTypeToDocumentationTabType[configurationType];
        if (!documentationTabType) {
          return;
        }

        // Collect all tag IDs from this configuration's user inputs
        const allTagIds = extractTagIdsFromUserInputs(userInputs);

        // Add configuration ID to arrays for each tag
        allTagIds.forEach((tagId) => {
          // Initialize tag entry if it doesn't exist
          if (!tagArrays[tagId]) {
            tagArrays[tagId] = {};
          }

          // Initialize array for this documentation tab type if it doesn't exist
          if (!tagArrays[tagId][documentationTabType]) {
            tagArrays[tagId][documentationTabType] = [];
          }

          // Add configuration ID to array if not already present
          if (!tagArrays[tagId][documentationTabType].includes(metadataId)) {
            tagArrays[tagId][documentationTabType].push(metadataId);
          }
        });
      });

      state.tagsAndUsersCountSummary[crmOrgId] = tagArrays as any;
    },
  },
});

export const {
  setTagsList,
  setTagConfigurations,
  addTag,
  updateTag,
  deleteTag,
  clearTagConfigurations,

  generateTagsAndUsersSummary,
  setParserItemsMap,
  addItemsToParserItemsMap,

  setSingleConfigurationUserInputs,
  setUserInputsByMetadataId,
  deleteTagFromConfigurationUserInputs,
} = userInputsSlice.actions;

export const selectTagsList = (state: RootState) => state.userInputs.tagsList;
export const selectTagById = (tagId: string) => (state: RootState) =>
  state.userInputs.tagsList.find((tag) => tag.id === tagId);

export const selectTagConfigurations = (tagId: string) => (state: RootState) =>
  state.userInputs.tagsConfigurations[tagId];

export const selectUserInputsByMetadataId = (metadataId: string) => (state: RootState) =>
  state.userInputs.userInputsByMetadataId?.[metadataId];

export const selectCountSummaryByCrmOrgId = (crmOrgId: string) => (state: RootState) =>
  state.userInputs.tagsAndUsersCountSummary?.[crmOrgId];

export default userInputsSlice.reducer;
